import { Box, Button, Chip, Grid, Stack } from '@mui/material';
import React, { Component, createRef } from 'react';
import { FormTextArea } from '../../common/Form/FormTextArea';
import { MAX_STUDENT_UIN_COUNT_LIMIT } from '../../common/constants/sam';

const DELIM_REGEX = /[,\.;\n\s]+/; // comma, period, semicolon, newline, space
const NUMERIC_REGEX = /^\d+$/; // digits only

class Filter extends Component {
  inputRef = createRef();

  state = {
    input: '',
    uins: [],
  };

  // Focus and move caret to the end so it appears after the last chip
  focusInput = () => {
    const el = this.inputRef.current;
    if (el) {
      el.focus();
      const len = el.value?.length ?? 0;
      try {
        el.setSelectionRange(len, len);
      } catch {}
    }
  };

  isAtLimit = (uins) => uins.length >= MAX_STUDENT_UIN_COUNT_LIMIT;

  emitUins = (next) => {
    const set = new Set(next);
    this.setState({ uins: Array.from(set) });
  };

  addUin = (raw) => {
    const uin = (raw || '').trim();
    if (!uin || this.isAtLimit(this.state.uins)) return;
    this.emitUins([...this.state.uins, uin]);
  };

  commitInput = () => {
    const { input } = this.state;
    if (!input.trim() || this.isAtLimit(this.state.uins)) return;
    this.addUin(input);
    this.setState({ input: '' }, () => {
      this.focusInput();
      this.scrollToBottom();
    });
  };

  isDelimiterKey = (key) =>
    key === ',' || key === '.' || key === ';' || key === 'Enter' || key === ' ';

  handleKeyDown = (e) => {
    if (this.isDelimiterKey(e.key)) {
      e.preventDefault(); // don't insert the delimiter
      this.commitInput();
      return;
    }
    // Backspace removes last chip when input is empty
    if (e.key === 'Backspace' && !this.state.input) {
      const next = this.state.uins.slice(0, -1);
      if (next.length !== this.state.uins.length)
        this.emitUins(next, () => {
          this.focusInput();
          this.scrollToBottom();
        });
    }
  };

  handleChange = (value) => {
    if (!this.isAtLimit(this.state.uins)) {
      this.setState({ input: value });
    }
  };

  handlePaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (!text) return;
    e.preventDefault();

    const { uins, input } = this.state;

    const pieces = (input + text) // Add an extra space for the last UIN to convert into chip
      .split(DELIM_REGEX)
      .map((s) => s.trim());

    let next = [...uins];
    for (const p of pieces) {
      if (!p) continue;
      if (this.isAtLimit(next)) break;
      next.push(p);
    }
    this.emitUins(next);

    this.setState(
      {
        input: '',
      },
      () => {
        this.focusInput();
        this.scrollToBottom();
      },
    );
  };

  scrollToBottom = () => {
    const el = this.inputRef.current;
    const root = el?.closest?.('.MuiOutlinedInput-root');
    if (root) root.scrollTop = root.scrollHeight;
  };

  handleDelete = (idx) => {
    const next = this.state.uins.slice();
    next.splice(idx, 1);
    this.emitUins(next, () => {
      this.focusInput();
      this.scrollToBottom();
    });
  };

  //θ(1) Resets all selected fields
  onClear = () => {
    this.setState({
      input: '',
      uins: [],
    });
  };

  handleSearch = () => {
    this.props.onSearch(this.state.uins);
  };

  render() {
    const id = this.props.id;
    const atLimit = this.state.uins.length === MAX_STUDENT_UIN_COUNT_LIMIT;
    const invalidUins = this.state.uins.filter((t) => !NUMERIC_REGEX.test(t));
    const hasError = invalidUins.length > 0;

    return (
      <React.Fragment>
        <Stack direction={'column'} gap={2}>
          {
            <Grid
              container
              direction={{ xs: 'column', sm: 'column', xl: 'row' }}
              spacing={2}
              sx={{
                justifyContent: 'center',
              }}
            >
              <Grid item xs={12} xl={12}>
                <FormTextArea
                  id={`${id}__textArea_studentUins`}
                  label={'Bulk Search'}
                  inputGridProps={{ xs: 12 }}
                  rows={1}
                  labelGridProps={{ xs: 12 }}
                  onKeyDown={this.handleKeyDown}
                  inputRef={this.inputRef}
                  error={hasError || atLimit}
                  errorText={
                    hasError
                      ? 'Search only available for UINs, remove items that are not UINs.'
                      : atLimit
                        ? `Maximum search limit of ${MAX_STUDENT_UIN_COUNT_LIMIT} students reached.`
                        : ''
                  }
                  value={this.state.input}
                  helperText="Search up to 1000 students with UIN separated with semi colon(;), comma(,), dot(.), line break or space."
                  size="small"
                  textAlign={{ xs: 'start' }}
                  onChange={this.handleChange}
                  charCountEnabled={false}
                  className="myOdu__chipScrollBox"
                  InputProps={{
                    startAdornment: (
                      <Box
                        onClick={() => {
                          this.focusInput();
                          this.scrollToBottom();
                        }}
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          py: 0.5,
                        }}
                      >
                        {this.state.uins.map((uin, index) => {
                          const isValidUin = NUMERIC_REGEX.test(uin);
                          return (
                            <Chip
                              key={`${uin}-${index}`}
                              className={`uinChip ${!isValidUin ? 'error' : ''}`}
                              label={uin}
                              onClick={() => this.handleDelete(index)}
                              variant="outlined"
                              sx={{ m: 0 }}
                            />
                          );
                        })}
                      </Box>
                    ),
                    onPaste: this.handlePaste,
                    inputProps: {
                      onInput: this.scrollToBottom,
                    },
                  }}
                />
              </Grid>

              <Grid
                container
                item
                xs={12}
                xl={12}
                justifyContent={'flex-end'}
                gap={1}
                direction={{ xs: 'column-reverse', xl: 'row' }}
              >
                <Grid item>
                  <Button
                    id={`${id}__button_clearFields`}
                    className="myOdu__button secondary"
                    variant="outlined"
                    onClick={this.onClear}
                    fullWidth
                    disabled={!this.state.uins.length && !this.state.input}
                  >
                    {'Clear'}
                  </Button>
                </Grid>

                <Grid
                  item
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    id={`${id}__button`}
                    className="myOdu__button primary"
                    variant="outlined"
                    onClick={this.handleSearch}
                    disabled={
                      this.props.disablePrimaryButton || !this.state.uins.length || hasError
                    }
                    fullWidth
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          }
        </Stack>
      </React.Fragment>
    );
  }
}

export default Filter;
